import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { INVESTMENT_JOIN_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/useInvestmentJoinAndAllocationController';
import { JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationChecksComplete/JoinAndAllocationChecksComplete';
import { JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationPersonalDetails';
import { shouldIncludeInOtherDetails } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/utils/otherDetails';
import { useWizardStepRegistry } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardNavigationIntent } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import {
	CustomDetailDataTypeEnum,
	GetShareholderCustomDetailRes,
	MipKycSettingResponseDto,
} from '@dealstack/shared/lib/generated';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

export const useStandaloneKycSteps = (
	kycSetting?: MipKycSettingResponseDto | null,
	isPiv = false,
	customDetails?: GetShareholderCustomDetailRes | null,
	hasAudienceForms?: boolean
) => {
	// Every conditional step is absent until its answer arrives, so the list is only trustworthy
	// once all three have. Until then it is a placeholder: render it, but never navigate on it.
	const areStepsResolved =
		kycSetting !== undefined && customDetails !== undefined && hasAudienceForms !== undefined;
	const tInvestor = useTranslationInvestor();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { dealId, mipId, kycWorkflowId } = useParams();
	const kycFlowPath = generatePath(ROUTES.STANDALONE_KYC, {
		dealId: dealId ?? '',
		mipId: mipId ?? '',
		kycWorkflowId: kycWorkflowId ?? '',
	});
	const stepRegistry = useWizardStepRegistry();

	const [isContinueDisabled, setIsContinueDisabled] = useState(false);

	const steps = useMemo(() => {
		const pd = INVESTMENT_JOIN_ROUTES.PERSONAL_DETAILS;
		const cc = INVESTMENT_JOIN_ROUTES.CHECKS_COMPLETE;
		const isProofOfIdentityRequired = kycSetting?.isProofOfIdentityRequired ?? true;
		const isProofOfAddressRequired = kycSetting?.isProofOfAddressRequired ?? true;
		const isProofOfInvestmentVehicleRequired =
			kycSetting?.isProofOfInvestmentVehicleRequired ?? true;
		const isBankAccountRequired = isPiv
			? kycSetting?.isVehicleBankAccountRequired ?? false
			: kycSetting?.isInvestorBankAccountRequired ?? false;
		const anyDeclarationRequired =
			(kycSetting?.isBeneficialOwnershipRequired ?? true) ||
			(kycSetting?.isSourceOfFundsRequired ?? true) ||
			(kycSetting?.isPepRequired ?? true);

		const hasRequiredPredefined =
			kycSetting?.isTaxResidenceRequired ||
			kycSetting?.isTinRequired ||
			kycSetting?.isPlaceOfBirthRequired ||
			kycSetting?.isCountryOfBirthRequired ||
			kycSetting?.isEmployeeIdRequired ||
			kycSetting?.isCitizenshipRequired;

		const hasCustomFields = customDetails?.definitions?.some(
			(field) =>
				field.dataType !== CustomDetailDataTypeEnum.BANK_ACCOUNT &&
				field.dataType !== CustomDetailDataTypeEnum.FORM &&
				field.dataType !== CustomDetailDataTypeEnum.PRE_FILLABLE_FORM &&
				field.dataType !== CustomDetailDataTypeEnum.TAX_RESIDENCY &&
				field.dataType !== CustomDetailDataTypeEnum.TIN &&
				field.dataType !== CustomDetailDataTypeEnum.COUNTRY_OF_BIRTH &&
				shouldIncludeInOtherDetails(field.type, isPiv)
		);

		const showOtherDetails = hasRequiredPredefined || hasCustomFields;

		const hasForms = hasAudienceForms ?? true;

		const allSteps = [
			{
				title: tInvestor('investment.instructions'),
				route: `${cc}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.INSTRUCTIONS}`,
			},
			{
				title: tInvestor('investment.basic_info'),
				route: `${pd}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.PERSONAL_INFORMATION}`,
			},
			{
				title: tInvestor('investment.investment_vehicle'),
				route: `${pd}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.INVESTMENT_VEHICLE_INFORMATION}`,
			},
			...(isBankAccountRequired
				? [
						{
							title: tInvestor('investment.join.sidebar.steps.bank_account'),
							route: `${pd}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.BANK_ACCOUNT}`,
						},
					]
				: []),
			...(showOtherDetails
				? [
						{
							title: tInvestor('investment.join.sidebar.steps.others'),
							route: `${pd}${JOINER_ALLOCATION_PERSONAL_DETAILS_ROUTES.OTHER_DETAILS}`,
						},
					]
				: []),
			...(isProofOfIdentityRequired || isProofOfAddressRequired
				? [
						{
							title: tInvestor('investment.personal_ids'),
							route: `${cc}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.PERSONAL}`,
						},
					]
				: []),
			...(isProofOfInvestmentVehicleRequired && isPiv
				? [
						{
							title: tInvestor('investment.company_ids'),
							route: `${cc}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.COMPANY}`,
						},
					]
				: []),
			...(hasForms
				? [
						{
							title: tInvestor('investment.join.sidebar.steps.forms'),
							route: `${cc}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.KYC_FORMS}`,
						},
					]
				: []),
			...(anyDeclarationRequired
				? [
						{
							title: tInvestor('investment.join.sidebar.steps.declarations'),
							route: `${cc}${JOIN_ALLOCATION_CHECKS_COMPLETE_ROUTES.DECLARATIONS}`,
						},
					]
				: []),
		];

		return allSteps.map((step, index) => ({ ...step, key: index }));
	}, [kycSetting, isPiv, customDetails, tInvestor, hasAudienceForms]);

	const matchedStepKey = useMemo(
		() => steps.find((s) => pathname.includes(s.route))?.key ?? null,
		[pathname, steps]
	);

	const activeStepKey = matchedStepKey ?? 0;

	const activeStep = useMemo(
		() => (matchedStepKey === null ? undefined : steps[matchedStepKey]),
		[steps, matchedStepKey]
	);
	const nextStep = useMemo(
		() => (matchedStepKey === null ? undefined : steps[matchedStepKey + 1]),
		[steps, matchedStepKey]
	);
	const previousStep = useMemo(
		() => (matchedStepKey === null ? undefined : steps[matchedStepKey - 1]),
		[steps, matchedStepKey]
	);
	const isLastStep = matchedStepKey !== null && matchedStepKey === steps.length - 1;

	const navigateToStep = useCallback(
		(route: string, options?: { replace?: boolean }) => {
			const to = `${kycFlowPath}${route}`;
			if (options) {
				navigate(to, options);
				return;
			}
			navigate(to);
		},
		[navigate, kycFlowPath]
	);

	const saveThenNavigate = useCallback(
		async (intent: WizardNavigationIntent, route: string | undefined) => {
			const result = await stepRegistry.saveActiveStep(intent);
			if (!result.ok || !route) {
				return;
			}
			if (route === ROUTES.DASHBOARD) {
				navigate(route);
			} else {
				navigateToStep(route);
			}
		},
		[stepRegistry, navigate, navigateToStep]
	);

	useEffect(() => {
		if (!areStepsResolved || matchedStepKey !== null) {
			return;
		}
		navigateToStep(steps[0].route, { replace: true });
	}, [areStepsResolved, matchedStepKey, steps, navigateToStep]);

	const [advanceFromRoute, setAdvanceFromRoute] = useState<string | null>(null);

	const onNextStep = useCallback(async () => {
		const result = await stepRegistry.saveActiveStep('next');
		if (!result.ok) {
			return;
		}
		setAdvanceFromRoute(activeStep?.route ?? null);
	}, [stepRegistry, activeStep]);

	useEffect(() => {
		if (advanceFromRoute === null) {
			return;
		}
		setAdvanceFromRoute(null);

		const index = steps.findIndex((step) => step.route === advanceFromRoute);
		const target = index === -1 ? undefined : steps[index + 1];

		if (target) {
			navigateToStep(target.route);
		} else {
			navigate(ROUTES.DASHBOARD);
		}
	}, [advanceFromRoute, steps, navigateToStep, navigate]);

	const onPreviousStep = useCallback(async () => {
		if (!previousStep) {
			return;
		}
		await saveThenNavigate('back', previousStep.route);
	}, [previousStep, saveThenNavigate]);

	const onSidebarNavigation = useCallback(
		async (key: number) => {
			const step = steps.find((s) => s.key === key);
			if (step) {
				navigateToStep(step.route);
			}
		},
		[steps, navigateToStep]
	);

	const sideBarItems = useMemo(
		() =>
			steps.map((s) => ({
				key: s.key,
				title: s.title,
				allowNavigation: s.key < activeStepKey,
				allowExpandByClick: false,
				subItems: [],
			})),
		[steps, activeStepKey]
	);

	return {
		steps,
		activeStep,
		activeStepKey,
		nextStep,
		previousStep,
		isLastStep,
		isContinueDisabled,
		setIsContinueDisabled,
		stepRegistry,
		onNextStep,
		onPreviousStep,
		onSidebarNavigation,
		sideBarItems,
	};
};
